import { match } from 'path-to-regexp';
import { hasScope } from '../../utils/users';
import { faAddressBook, faMessages, faRobot, faChartSimple, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { inject } from 'vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import {
    isHeightsWellnessTeam,
    isDucklingsTeam,
    isOptimizeUTeam,
    isStratusCleanTeam,
    isThePicklrTeam,
} from '~/utils/team';

export const navbarRoutes = (sparkSettings) => {
    const team = Spark.state.currentTeam;

    const FEATURES = inject('FEATURES');
    const { onCorporate, currentOrgUnit, currentGroup, roleOnCurrentOrgUnit, roleOnCurrentGroup, canUseMessageHub } =
        useCurrentGroup();

    const { isServiceRepresentative, ownsCurrentTeam, isAdminRoleOnCurrentTeam } = sparkSettings;

    const analytics = {
        name: 'Analytics',
        icon: faChartSimple,
        pathMatcher: match([
            '/analytics{/*path}',
            '/teams/:id/omni-analytics{/*path}',
            '/teams/:id/metabase-analytics',
        ]),
        enable: false,
    };

    // The Omni dashboards the backend exposes for the current team (slug + label + supported contexts).
    // Access is allow-list only, so an unmapped team gets an empty manifest and no analytics nav item.
    // Filter by the current context, mirroring the backend's precedence (group > org_unit > corporate).
    const omniContext = currentGroup.value?.group_id ? 'group' : currentOrgUnit.value?.id ? 'org_unit' : 'corporate';
    const omniAnalyticsItems = (Spark.state.omniDashboards ?? [])
        .filter((dashboard) => dashboard.contexts.includes(omniContext))
        .map((dashboard) => ({
            name: dashboard.label ?? ('corporate' === omniContext ? 'Brand Performance' : 'Local Performance'),
            href: dashboard.key
                ? `/teams/${team.id}/omni-analytics/${dashboard.key}`
                : `/teams/${team.id}/omni-analytics`,
            pathMatcher: match([
                dashboard.key ? `/teams/:id/omni-analytics/${dashboard.key}` : '/teams/:id/omni-analytics',
            ]),
            target: null,
            enable: FEATURES.omni_analytics,
        }));

    if (currentOrgUnit.value && 'agent' !== roleOnCurrentOrgUnit) {
        analytics.target = null;
        analytics.enable = true;
        analytics.items = [
            { name: 'Dashboard', href: '/analytics/dashboard', target: null, enable: true },
            { name: 'Preferred Contacts', href: '/analytics/preferred-contacts', target: null, enable: true },
            { name: 'Outbound Messages', href: '/analytics/outbound-messages', target: null, enable: true },
            ...omniAnalyticsItems,
        ];
    } else if (currentGroup.value && 'agent' !== roleOnCurrentGroup) {
        analytics.target = null;
        analytics.enable = true;
        analytics.items = [
            { name: 'Dashboard', href: '/analytics/dashboard', target: null, enable: true },
            { name: 'Affiliated Contacts', href: '/analytics/affiliated-contacts', target: null, enable: true },
            { name: 'Preferred Contacts', href: '/analytics/preferred-contacts', target: null, enable: true },
            { name: 'Outbound Messages', href: '/analytics/outbound-messages', target: null, enable: true },
            ...omniAnalyticsItems,
        ];
    } else if (hasScope('analytics:read') && onCorporate()) {
        analytics.target = null;
        analytics.enable = true;
        analytics.items = [
            { name: 'Dashboard', href: '/analytics/dashboard', target: null, enable: true },
            {
                name: 'Franchise Hub',
                href: '/analytics/franchise-hub',
                pathMatcher: match(['/analytics/franchise-hub{/*path}']),
                target: null,
                enable: FEATURES.contact_groups,
            },
            { name: 'Contacts', href: '/analytics/contacts', target: null, enable: true },
            { name: 'Entry Points', href: '/analytics/entry-points', target: null, enable: true },
            { name: 'Outbound Messages', href: '/analytics/outbound-messages', target: null, enable: true },
            {
                name: 'Workflows',
                href: '/workflow-builder/analytics',
                pathMatcher: match(['/workflow-builder/analytics{/*path}']),
                target: null,
                enable: FEATURES.workflow_builder,
            },
            ...omniAnalyticsItems,
        ];

        if (team.dashboard_url) {
            analytics.items.push({
                name: 'Custom',
                href: `/teams/${team.id}/metabase-analytics`,
                target: '_blank',
                enable: Boolean(ownsCurrentTeam || isAdminRoleOnCurrentTeam),
            });
        }
    } else if (team.dashboard_url && Boolean(ownsCurrentTeam || isAdminRoleOnCurrentTeam)) {
        analytics.href = `/teams/${team.id}/metabase-analytics`;
        analytics.target = '_blank';
        analytics.enable = Boolean(ownsCurrentTeam || isAdminRoleOnCurrentTeam);
    }

    let automations;

    if (FEATURES.flows || FEATURES.workflow_builder) {
        automations = {
            name: 'Automation',
            icon: faRobot,
            pathMatcher: match(['/automation{/*path}']),
            items: [
                {
                    name: 'Automations',
                    href: '/automation/event',
                    target: null,
                    pathMatcher: match(['/automation/event{/*path}']),
                    enable: true,
                },
                {
                    name: 'Flows',
                    href: '/automation/flows',
                    target: null,
                    enable: FEATURES.flows,
                    pathMatcher: match(['/automation/flows{/*path}']),
                },
                {
                    name: 'Workflow Builder',
                    href: '/workflow-builder',
                    target: null,
                    enable: FEATURES.workflow_builder,
                    pathMatcher: match(['/workflow_builder{/*path}']),
                },
            ],
            enable: onCorporate() && !isServiceRepresentative,
        };
    } else {
        automations = {
            name: 'Automations',
            icon: faRobot,
            href: '/automation/event',
            pathMatcher: match(['/automation{/*path}']),
            target: null,
            enable: onCorporate() && !isServiceRepresentative,
        };
    }

    const routes = [
        {
            name: 'Contacts',
            icon: faAddressBook,
            items: [
                {
                    name: 'All Contacts',
                    href: `/contacts`,
                    pathMatcher: match([
                        '/contacts',
                        '/contacts/1:id{/*path}',
                        '/contacts/2:id{/*path}',
                        '/contacts/3:id{/*path}',
                        '/contacts/4:id{/*path}',
                        '/contacts/5:id{/*path}',
                        '/contacts/6:id{/*path}',
                        '/contacts/7:id{/*path}',
                        '/contacts/8:id{/*path}',
                        '/contacts/9:id{/*path}',
                        '/contacts/new',
                    ]),
                    target: null,
                    enable: !currentOrgUnit.value?.id,
                },
                {
                    name: 'Segments',
                    href: `/contacts/segments`,
                    pathMatcher: match([
                        '/contacts/segments',
                        '/contacts/segments/new',
                        '/contacts/segments/1:id',
                        '/contacts/segments/2:id',
                        '/contacts/segments/3:id',
                        '/contacts/segments/4:id',
                        '/contacts/segments/5:id',
                        '/contacts/segments/6:id',
                        '/contacts/segments/7:id',
                        '/contacts/segments/8:id',
                        '/contacts/segments/9:id',
                    ]),
                    target: null,
                    enable:
                        (onCorporate() && !isServiceRepresentative) ||
                        (!onCorporate() &&
                            ['admin', 'owner', 'marketer', 'blast'].includes(
                                roleOnCurrentOrgUnit || roleOnCurrentGroup
                            )),
                },
                {
                    name: 'Segment Templates',
                    href: `/contacts/segments/templates`,
                    pathMatcher: match(['/contacts/segments/templates{/*path}']),
                    target: null,
                    enable: onCorporate() && FEATURES.contact_groups,
                },
                {
                    name: 'External Lists',
                    href: `/contacts/external-contact-lists`,
                    pathMatcher: match(['/contacts/external-contact-lists{/*path}']),
                    target: null,
                    enable: onCorporate() && !isServiceRepresentative && FEATURES.external_contact_lists,
                },
                {
                    name: 'Tags',
                    href: `/contacts/tags`,
                    target: null,
                    enable: onCorporate() && !isServiceRepresentative,
                },
                {
                    name: 'Custom Attributes',
                    href: `/contacts/custom-attributes`,
                    target: null,
                    enable: onCorporate() && !isServiceRepresentative,
                },
                {
                    name: 'Imports',
                    href: `/contacts/import`,
                    pathMatcher: match(['/contacts/import{/*path}']),
                    target: null,
                    enable:
                        (onCorporate() && !isServiceRepresentative) ||
                        (!onCorporate() && ['admin', 'owner'].includes(roleOnCurrentGroup)),
                },
                {
                    name: 'Export',
                    href: `/contacts/export/new`,
                    pathMatcher: match(['/contacts/export{/*path}']),
                    target: null,
                    enable: onCorporate(),
                },
            ],
            pathMatcher: match(['/contacts{/*path}']),
            target: null,
            enable: true,
        },
        {
            name: 'Messaging',
            icon: faMessages,
            items: [
                {
                    name: 'Message Hub',
                    href: '/message-hub',
                    pathMatcher: match(['/message-hub{/*path}']),
                    target: null,
                    enable: canUseMessageHub.value,
                },
                {
                    name: 'Quick Blast',
                    href: `/messaging/quick-blast`,
                    target: null,
                    enable:
                        onCorporate() ||
                        ['admin', 'owner', 'marketer', 'blast'].includes(roleOnCurrentOrgUnit || roleOnCurrentGroup),
                },
                {
                    name: 'Blast Forecast',
                    href: '/messaging/forecast',
                    pathMatcher: match(['/messaging/forecast']),
                    target: null,
                    enable: !currentGroup.value,
                },
                {
                    name: 'Campaigns',
                    href: `/messaging/campaigns`,
                    pathMatcher: match(['/messaging/campaigns{/*path}']),
                    target: null,
                    enable:
                        (onCorporate() && !isServiceRepresentative) ||
                        (!onCorporate() &&
                            ['admin', 'owner', 'marketer', 'blast'].includes(
                                roleOnCurrentOrgUnit || roleOnCurrentGroup
                            )),
                },
                {
                    name: 'Reserved Dates',
                    href: '/messaging/reserved-dates',
                    pathMatcher: match(['/messaging/reserved-dates{/*path}']),
                    target: null,
                    enable: onCorporate() && FEATURES.contact_groups,
                },
                {
                    name: 'Snippets',
                    href: `/messaging/snippets`,
                    target: null,
                    enable: true,
                },
                {
                    name: 'Snippet Templates',
                    href: `/messaging/snippets/templates`,
                    pathMatcher: match(['/messaging/snippets/templates{/*path}']),
                    target: null,
                    enable: onCorporate() && FEATURES.contact_groups,
                },
            ],
            pathMatcher: match(['/messaging{/*path}', '/message-hub{/*path}']),
            // Message Hub lays out its own page, so it gets no subnav.
            subnavPathMatcher: match(['/messaging{/*path}']),
            target: null,
            enable: true,
        },
        automations,
        analytics,
        {
            name: 'Support',
            icon: faCircleInfo,
            pathMatcher: match(['support']),
            enable: true,
            items: [
                {
                    name: 'Knowledge Base',
                    href: 'https://explore.voxie.com/voxiesupport',
                    target: '_blank',
                    enable: true,
                },
                {
                    name: 'API Docs',
                    href: 'https://docs.voxie.com/reference/getting-started-with-your-api',
                    target: '_blank',
                    enable: true,
                },
                {
                    name: 'Chat',
                    onClick: () => {
                        window.HubSpotConversations.widget.open();
                    },
                    enable: !!window.HubSpotConversations,
                },
                {
                    name: 'Image Gallery',
                    href: 'https://www.voxie.com/resource-center/image-gallery/',
                    target: '_blank',
                    enable: true,
                },
                {
                    name: 'Resource Center',
                    href: 'https://heights-resources.lovable.app/',
                    target: '_blank',
                    enable: isHeightsWellnessTeam(team.id),
                },
                {
                    name: 'Resource Center',
                    href: 'https://ducklings.with-voxie.com/',
                    target: '_blank',
                    enable: isDucklingsTeam(team.id),
                },
                {
                    name: 'Resource Center',
                    href: 'https://optimizeu-training.with-voxie.com/',
                    target: '_blank',
                    enable: isOptimizeUTeam(team.id),
                },
                {
                    name: 'Resource Center',
                    href: 'https://stratusclean.with-voxie.com/',
                    target: '_blank',
                    enable: isStratusCleanTeam(team.id),
                },
                {
                    name: 'Resource Center',
                    href: 'https://thepicklr.with-voxie.com/',
                    target: '_blank',
                    enable: isThePicklrTeam(team.id),
                },
            ],
        },
    ];

    return routes
        .filter((route) => {
            return route && route.enable && !route.items?.every((item) => !item.enable);
        })
        .map((route) => {
            route.items = route.items?.filter((item) => item.enable);
            return route;
        });
};
